<template>
  <header class="BaseTemplateHeader bg-white flex items-center justify-between q-px-lg q-py-12">
    <span class="flex">
      <k-circle-icon
        v-if="$q.screen.lt.md"
        id="toggle_layer_drawer"
        name="filters"
        color="cool-gray-100"
        root-class="q-pa-sm cursor-pointer"
        size="20px"
        @click="$emit('toggle-layer-drawer')"
      />
    </span>

    <div
      class="BaseTemplateHeader__toolbox q-px-20 q-py-sm rounded-12 bg-off-white-10 flex items-center gap-md"
      :class="{ 'BaseTemplateHeader__toolbox--receipt': templateStore.isPrintTemplate }"
    >
      <p class="q-mb-none fs-13px fw-500 text-space-b-xd">{{ t('contents.add_content') }}</p>
      <span class="BaseTemplateHeader__separator"></span>
      <span class="flex">
        <k-icon
          class="cursor-pointer"
          size="24px"
          name="text"
          color="cool-gray-100"
          @click="
            hook.handleAddTextShapeContent({
              type: 'text',
              ...(templateStore.isDigitalMenu
                ? { overrideFields: { is_field: true, value: { field: '', title: 'Add Item' } } }
                : {}),
            })
          "
        />
        <k-tooltip :label="t('contents.add_text')" position="top" />
      </span>
      <span class="BaseTemplateHeader__separator"></span>
      <span class="flex">
        <k-icon
          class="cursor-pointer"
          size="24px"
          name="add-image"
          color="cool-gray-100"
          @click="hook.handleAddImageContent"
        />
        <k-tooltip :label="t('contents.add_image')" position="top" />
      </span>
      <span class="BaseTemplateHeader__separator"></span>

      <div class="flex item-center cursor-pointer relative">
        <span class="flex">
          <k-icon size="24px" name="shapes" color="cool-gray-100" />
          <k-tooltip :label="t('contents.add_shape')" position="top" />
        </span>
        <k-icon size="16px" name="arrow-down" color="cool-gray-70" />

        <q-popup-proxy>
          <div
            v-for="(shape, index) in shapes"
            :key="index"
            class="BaseTemplateHeader__shape q-px-md q-py-12 text-cool-gray-80 fs-13px fw-500 cursor-pointer"
            @click="
              hook.handleAddTextShapeContent({
                type: 'shape',
                overrideFields: shape.value,
              })
            "
          >
            {{ shape.label }}
          </div>
        </q-popup-proxy>
      </div>
    </div>

    <div class="flex items-center gap-md">
      <k-btn
        class="BaseTemplateHeader__preview-btn"
        size="md"
        :label="globalT('preview')"
        model="link"
        :iconable="false"
        @click="isPreviewOpen = true"
      />
      <k-btn
        size="sm"
        :label="globalT('cancel')"
        model="secondary"
        :loading="isLoading"
        :to="
          templateStore.isDigitalMenu
            ? { name: 'DevicesAndProfiles', hash: '#digitalMenuTemplates' }
            : { name: 'PrintingSetups', hash: '#printTemplates' }
        "
      />
      <k-btn size="sm" :label="globalT('save')" :loading="isLoading" @click="hook.handleSaveTemplateChanges" />
    </div>

    <k-dialog
      v-model="isPreviewOpen"
      :title="globalT('preview')"
      :container-style="{ maxWidth: `${templateStore.templateWidth + PREVIEW_DIALOG_PADDING}px` }"
    >
      <template #content>
        <!-- The receipt as it prints. inert blocks clicks, focus and typing, so the preview can't change it -->
        <div class="flex justify-center" inert>
          <BaseTemplateMain has-printer />
        </div>
      </template>
    </k-dialog>
  </header>
</template>

<script setup>
import useBaseTemplateHeaderHook from './BaseTemplateHeaderHook'
import BaseTemplateMain from '../main/BaseTemplateMain.vue'

defineOptions({ name: 'BaseTemplateHeader' })

defineEmits(['toggle-layer-drawer'])

// k-dialog's container (q-pa-sm) and content (q-px-md) padding around the receipt
const PREVIEW_DIALOG_PADDING = 48

const { shapes, isLoading, isPreviewOpen, templateStore, t, globalT, ...hook } = useBaseTemplateHeaderHook()
</script>

<style lang="sass" scoped>
@import "src/shared/styles/variables.sass"
@import "src/shared/styles/mixins.sass"

.BaseTemplateHeader
  position: sticky
  top: 0
  z-index: 1
  border-bottom: 2px solid var(--off-white-20)
  @include element('preview-btn')
    min-width: 80px
  @include element('toolbox')
    transform: translateX(-10%)
    @include at-gt-md
      transform: translateX(41%)
    @include state-modifier('receipt')
      @include at-gt-md
        transform: translateX(41%)
  @include element('separator')
    height: 24px
    width: 1px
    background: var(--cool-gray-30)
  @include element('shape')
    width: 220px
    border-bottom: 1px solid var(--off-white-20)

    &::last-of-type
      border: none
</style>
